import { z } from "zod";

type PhoneValidatorOptions = {
  minLength: number;
  maxLength: number;
  startWith: string[]; // local-only start rules (supports wildcard '*')
  simpleError?: boolean; // simplify error to length-only message
  msgType: "phone" | "digits";
};

type AccountNoValidatorOptions = Omit<PhoneValidatorOptions, "msgType">;

type ValidatorSchemaType = {
  username: z.ZodString;
  phone: (options: PhoneValidatorOptions) => z.ZodString;
  otp: z.ZodString;
  email: z.ZodString;
  legalName: z.ZodString;
  firstName: z.ZodString;
  accountName: z.ZodString;
  lastName: z.ZodString;
  secretCode: z.ZodString;
  payment: {
    eWallet: {
      accountNo: (options: AccountNoValidatorOptions) => z.ZodString;
      accountType: z.ZodString;
      accountKey: z.ZodString;
      isDefault: z.ZodOptional<z.ZodBoolean>;
    };
    bank: {
      bankKey: z.ZodString;
      accountNo: z.ZodString;
      accountNoDefault: z.ZodString;
      accountNoINR: z.ZodString;
      ifsc: z.ZodString;
    };
  };
  loginPassword: z.ZodString;
  loginPin: z.ZodString;
  registerPassword: z.ZodString;
  pan: z.ZodString;
  upiId: z.ZodString;
  cpf: z.ZodString;
  curp: z.ZodString;
  clabeNumber: z.ZodString;
  pixKeyType: z.ZodEnum<["cpf", "email", "phone", "evp"]>;
  pixKeyCpf: z.ZodString;
  pixKeyEmail: z.ZodString;
  pixKeyPhone: (options: PhoneValidatorOptions) => z.ZodString;
  pixKeyRandom: z.ZodString;
  pidType: z.ZodEnum<["dni", "ce", "pen_ce", "cop_ce", "pen_pas", "ruc", "cc", "nit", "cop_pas"]>;
  pid: {
    // PEN
    dni: z.ZodString;
    pen_ce: z.ZodString;
    pen_pas: z.ZodString;
    ruc: z.ZodString;
    // COP
    cc: z.ZodString;
    nit: z.ZodString;
    cop_pas: z.ZodString;
    cop_ce: z.ZodString;
  };
  cci: z.ZodString;
};

const MEMBER_NAME_REGEX = /^[\p{L}\p{M}\p{N}.@`]+(?:[\s'\-/(),@`]+[\p{L}\p{M}\p{N}.@`]+)*$/u;
const buildMemberNameValidator = (requiredError?: string) =>
  z
    .string(requiredError ? { required_error: requiredError } : undefined)
    .trim()
    .min(1, requiredError ?? "required")
    .regex(MEMBER_NAME_REGEX, "name is not valid");

export const OTP_GET_CODE_HINT_MESSAGE = "otp is required. click on get code";

export const ValidatorSchema: ValidatorSchemaType = {
  username: z
    .string()
    .min(4, "4 13 characters")
    .max(13, "too long reduce to 13 characters or less")
    .regex(/^\S+$/, "space not allowed please remove")
    .regex(/^\w+$/, "special character not allowed please remove"),
  loginPassword: z.string().min(4, "minimum 4 characters"),
  loginPin: z.string().min(4, "PIN length is invalid"),
  registerPassword: z
    .string()
    .trim()
    .min(4, "minimum 4 characters")
    .regex(/^\S+$/, "spaces not allowed please remove"),
  phone: (options: PhoneValidatorOptions) => {
    const { minLength, maxLength, startWith, simpleError, msgType } = options;
    let errMsgStartWith = "";
    let errMsgDigitsOnly = "";
    const isSingleLength = minLength === maxLength;
    switch (msgType) {
      case "phone":
        if (isSingleLength) {
          // Display single length message, no need to display startWith
          errMsgDigitsOnly = "phone number must be {{minLength}} digits";
        } else {
          // Display multiple length message, no need to display startWith
          errMsgDigitsOnly = "phone number must be {{minLength}}-{{maxLength}} digits";
        }

        errMsgStartWith = simpleError ? errMsgDigitsOnly : "phone number should start with {{startWith}}";
        break;
      case "digits":
        if (isSingleLength) {
          // Display single length message, need to display startWith
          errMsgStartWith = simpleError
            ? "enter a {{minLength}}-digit number"
            : "enter a {{minLength}}-digit number starting with {{startWith}}";
          errMsgDigitsOnly = errMsgStartWith;
        } else {
          // Display multiple length message, no need to display startWith
          errMsgStartWith = simpleError
            ? "enter a {{minLength}}-{{maxLength}} digit number"
            : "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
          errMsgDigitsOnly = errMsgStartWith;
        }
        break;
    }

    const startWithRegex = buildStartWithRegex(startWith);
    const digitRegex = buildLocalDigitLengthRegexes(minLength, maxLength, startWith);

    return z.string().regex(startWithRegex, errMsgStartWith).regex(digitRegex, errMsgDigitsOnly);
  },
  otp: z.string().min(1, OTP_GET_CODE_HINT_MESSAGE),
  legalName: buildMemberNameValidator("required"),
  firstName: buildMemberNameValidator("required"),
  accountName: buildMemberNameValidator("required"),
  lastName: buildMemberNameValidator("required"),
  payment: {
    eWallet: {
      accountNo: (options: AccountNoValidatorOptions) => {
        const { minLength, maxLength, startWith, simpleError } = options;
        const startWithRegex = buildStartWithRegex(startWith);
        const accountNoRegex = buildLocalDigitLengthRegexes(minLength, maxLength, startWith);
        const isSingleLength = minLength === maxLength;
        let errMsg;
        if (isSingleLength) {
          errMsg = simpleError
            ? "enter a {{minLength}}-digit number"
            : "enter a {{minLength}}-digit number starting with {{startWith}}";
        } else {
          errMsg = simpleError
            ? "enter a {{minLength}}-{{maxLength}} digit number"
            : "enter a {{minLength}}-{{maxLength}} digit number starting with {{startWith}}";
        }
        return z
          .string({
            required_error: "required",
          })
          .min(1, "required")
          .regex(startWithRegex, errMsg)
          .regex(accountNoRegex, errMsg);
      },
      accountType: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      accountKey: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      isDefault: z.boolean().optional(),
    },
    bank: {
      bankKey: z
        .string({
          required_error: "required",
        })
        .min(1, "required"),
      accountNo: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{5,34}$/, "account number must be 5-34 digits"),
      accountNoDefault: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{5,34}$/, "account number must be 5-34 digits"),
      accountNoINR: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .regex(/^\d+$/, "account number must be digits only")
        .regex(/^\d{6,19}$/, "account number must be 6-19 digits"),
      ifsc: z
        .string({
          required_error: "required",
        })
        .min(1, "required")
        .length(11, "ifsc code must be 11 characters")
        .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, "ifsc code is not valid"),
    },
  },
  pan: z
    .string({ required_error: "required" })
    .min(1, "required")
    .length(10, "pan number must be 10 characters")
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "pan number is not valid"),
  upiId: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^[\w.-]{2,256}@[a-zA-Z0-9_]{2,64}$/i, "upi id is not valid"),
  cpf: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/, "cpf number must be 11 digits"),
  curp: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .length(18, "curp number must be 18 characters")
    .regex(/^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]{2}$/, "curp number is not valid"),
  clabeNumber: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d+$/, "clabe must be digits only")
    .regex(/^\d{18}$/, "clabe must be 18 digits"),
  pixKeyType: z.enum(["cpf", "email", "phone", "evp"]),
  pixKeyCpf: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$|^\d{11}$/, "cpf is not valid"),
  pixKeyEmail: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "email is not valid"),
  pixKeyPhone: (options: PhoneValidatorOptions) => ValidatorSchema.phone(options),
  pixKeyRandom: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .length(36, "random key must be 36 characters")
    .regex(/^[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}$/),
  pidType: z.enum(["dni", "ce", "pen_ce", "cop_ce", "pen_pas", "ruc", "cc", "nit", "cop_pas"], {
    required_error: "required",
    message: "required",
  }),
  pid: {
    dni: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    pen_ce: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    pen_pas: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    ruc: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    cop_ce: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    cc: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    nit: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
    cop_pas: z
      .string({
        required_error: "required",
      })
      .trim()
      .min(1, "required"),
  },
  cci: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^\d+$/, "cci number must be digits only")
    .regex(/^\d{20}$/, "cci number must be 20 digits"),
  email: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .email("email is not valid"),
  secretCode: z
    .string({
      required_error: "required",
    })
    .min(1, "required")
    .regex(/^[A-Za-z0-9]{8}$/, "please enter a valid code"),
};

// Build local full-match regex from an array of masks and target lengths
const buildLocalDigitLengthRegexes = (minLength: number, maxLength: number, startWithMasks: string[]): RegExp => {
  const lengths: number[] = [];
  for (let i = minLength; i <= maxLength; i++) lengths.push(i);
  const regs: string[] = [];

  // if no startWith, return length regex only
  if (startWithMasks.length === 0) {
    lengths.forEach((length) => {
      regs.push(`^\\d{${length}}$`);
    });
  } else {
    // if startWith, return startWith regex and length regex
    startWithMasks.forEach((mask) => {
      const maskRegex = maskToRegex(mask);
      const maskDigits = countMaskDigits(mask);
      lengths.forEach((length) => {
        const bodyLen = length - maskDigits;
        if (bodyLen >= 0) regs.push(`^${maskRegex}\\d{${bodyLen}}$`);
      });
    });
  }

  return new RegExp(regs.join("|"));
};

// Build a start-with regex for local masks only
const buildStartWithRegex = (startWithMasks: string[]): RegExp => {
  const localPart = startWithMasks.map((m) => maskToRegex(m)).join("|");
  return new RegExp(`^(?:${localPart})`);
};

const maskToRegex = (mask: string): string => {
  // Escape regex special characters first, then replace escaped '*' back to \\d
  const escaped = escapeRegex(mask);
  return escaped.replace(/\\\*/g, "\\d");
};

const countMaskDigits = (mask: string): number => {
  // '*' counts as one digit; other characters count as themselves
  return mask.replace(/\*/g, "0").length;
};

const escapeRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
